import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  ButtonBase,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  MonetizationOn as MoneyIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import logo from '../../assets/images/logo.png';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth';
import { getAvatarUrl } from '../../utils/helpers';

const Navbar = ({ onDrawerToggle, onOpenProfile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showBalance, setShowBalance] = useState(true);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: '#0f172a',
        backgroundImage: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
        {/* Left Section: Logo & Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
            <Box
            onClick={() => navigate('/home')}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              cursor: 'pointer',
              gap: { xs: 1, sm: 1.5 },
              '&:hover': {
                '& img': { filter: 'drop-shadow(0 0 20px rgba(0, 217, 255, 0.5))', transform: 'scale(1.05)' },
                '& .brand-text': { opacity: 0.8 }
              }
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="GPT Team Logo"
              sx={{
                height: { xs: 35, sm: 50, md: 60 },
                transition: 'all 0.3s ease'
              }}
            />
            <Typography
              className="brand-text"
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.85rem', md: '1.1rem' },
                fontWeight: 900,
                letterSpacing: '0.5px',
                background: 'linear-gradient(90deg, #fff 0%, #00D9FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                lineHeight: 1.2,
                display: { xs: 'block', mini: 'none' } // Use hide on extremely small if needed
              }}
            >
              GPT Team Manager
            </Typography>
          </Box>
        </Box>

        {/* Right Section: User Info, Balance, Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
          {user ? (
            <>
              {/* User Profile Hooked to Sidebar replacement style */}
              <ButtonBase
                onClick={handleProfileMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '20px',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#00D9FF',
                    color: '#0f172a',
                    fontSize: 14,
                    fontWeight: 700
                  }}
                  src={getAvatarUrl(user?.avatar)}
                >
                  {user?.username?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                  <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
                    {user?.username}
                  </Typography>
                </Box>
                <ArrowDownIcon sx={{ color: '#64748b', fontSize: 18, display: { xs: 'none', sm: 'block' } }} />
              </ButtonBase>

              {/* Balance Section - Horizontal Alignment & Visibility Toggle */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                }}
              >
                <MoneyIcon sx={{ color: '#00D9FF', fontSize: 20 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
                    Số dư:
                  </Typography>
                  <Typography sx={{ color: '#00D9FF', fontWeight: 700, fontSize: 14 }}>
                    {showBalance ? `${formatCurrency(user?.wallet?.balance)} đ` : '******'}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setShowBalance(!showBalance)}
                  sx={{ color: '#64748b', ml: 0.5 }}
                >
                  {showBalance ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
              </Box>

              {/* Nạp tiền Button - Hidden on mobile/tablet as requested */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/deposit')}
                sx={{
                  display: { xs: 'none', md: 'flex' }, // Ẩn trên xs và sm
                  bgcolor: '#00D9FF',
                  color: '#0f172a',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: { xs: 1, sm: 2 },
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#00b8e6',
                    boxShadow: '0 0 15px rgba(0, 217, 255, 0.5)'
                  }
                }}
              >
                <Typography sx={{ fontWeight: 'inherit' }}>Nạp tiền</Typography>
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#00D9FF',
                color: '#0f172a',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: '#00b8e6',
                  boxShadow: '0 0 15px rgba(0, 217, 255, 0.5)'
                }
              }}
            >
              Đăng nhập
            </Button>
          )}
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                bgcolor: '#1e293b',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.05)',
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  fontSize: 14,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)'
                  }
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{user?.fullName || user?.username}</Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>{user?.email}</Typography>
            </Box>
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
            <MenuItem onClick={onOpenProfile}>
              <PersonIcon sx={{ mr: 1.5, fontSize: 20, color: '#94a3b8' }} /> Tài khoản
            </MenuItem>
            <MenuItem onClick={() => navigate('/deposit')}>
              <WalletIcon sx={{ mr: 1.5, fontSize: 20, color: '#94a3b8' }} /> Ví tiền
            </MenuItem>
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
            <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} /> Đăng xuất
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
